import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
/**
 * Etailize Helpers Utility
 * 
 * Provides utility functions for processing marketplace data,
 * field configurations, validation rules, and state management.
 */

// Supported field types for etailize authentication
const SUPPORTED_FIELD_TYPES = ['Text', 'Password', 'MarketplaceOption', 'OAuthButton'];

/**
 * Validate and normalize field type
 * @param {Object} field - Field configuration
 * @returns {string} - Validated field type
 */
const validateFieldType = (field) => {
    const fieldType = field.fieldType || field.type || 'Text';
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    if (!SUPPORTED_FIELD_TYPES.includes(fieldType)) {
        if (isDevelopment) {
            console.warn(`Unknown field type "${fieldType}" for field "${field.name}". Supported types:`, SUPPORTED_FIELD_TYPES);
        }
        return 'Text'; // Fallback to Text type
    }
    
    return fieldType;
};

/**
 * Extract marketplace capabilities from marketplace connection definition
 * @param {Object} marketplace - Marketplace connection definition
 * @returns {Object} - Processed capabilities object
 */
export const getMarketplaceCapabilities = (marketplace) => {
    if (!marketplace) {
        return { send: [], receive: [], all: [] };
    }

    // Check for explicit propagation_types field
    if (marketplace.propagation_types) {
        const send = marketplace.propagation_types.send || [];
        const receive = marketplace.propagation_types.receive || [];
        return {
            send: Array.isArray(send) ? send : [],
            receive: Array.isArray(receive) ? receive : [],
            all: [...(Array.isArray(send) ? send : []), ...(Array.isArray(receive) ? receive : [])]
        };
    }

    // Check for propagations field (alternative format)
    if (marketplace.propagations) {
        const propagations = Array.isArray(marketplace.propagations) ? marketplace.propagations : [];
        const send = propagations.filter(p => p.direction === 'send' || p.type === 'outbound').map(p => p.name || p.type);
        const receive = propagations.filter(p => p.direction === 'receive' || p.type === 'inbound').map(p => p.name || p.type);
        
        return {
            send,
            receive,
            all: [...send, ...receive]
        };
    }

    // Check for capabilities field
    if (marketplace.capabilities) {
        const capabilities = marketplace.capabilities;
        return {
            send: capabilities.send || capabilities.outbound || [],
            receive: capabilities.receive || capabilities.inbound || [],
            all: Object.values(capabilities).flat()
        };
    }

    // Fallback: infer from connection definition structure
    const inferredCapabilities = inferCapabilitiesFromDefinition(marketplace);
    return inferredCapabilities;
};

/**
 * Infer capabilities from marketplace connection definition structure
 * @param {Object} marketplace - Marketplace connection definition
 * @returns {Object} - Inferred capabilities
 */
const inferCapabilitiesFromDefinition = (marketplace) => {
    const send = [];
    const receive = [];

    // Check if marketplace typically supports order management
    if (marketplace.name?.toLowerCase().includes('marketplace') || 
        marketplace.name?.toLowerCase().includes('platform')) {
        receive.push('orders');
        send.push('stock', 'fulfillment');
    }

    // Check for specific marketplace patterns
    const marketplaceName = marketplace.name?.toLowerCase() || '';
    
    if (marketplaceName.includes('amazon') || marketplaceName.includes('ebay') || 
        marketplaceName.includes('bol.com') || marketplaceName.includes('zalando')) {
        receive.push('orders', 'returns');
        send.push('stock', 'prices', 'products', 'fulfillment');
    }

    // Default capabilities for most marketplaces
    if (send.length === 0 && receive.length === 0) {
        send.push('stock', 'fulfillment');
        receive.push('orders');
    }

    return {
        send,
        receive,
        all: [...send, ...receive]
    };
};

/**
 * Check if a sync option is supported by marketplace capabilities
 * @param {string} syncType - Type of sync (orders, stock, products, prices)
 * @param {string} direction - Direction (send, receive)
 * @param {Object} capabilities - Marketplace capabilities
 * @returns {boolean} - Whether the sync option is supported
 */
export const isSyncOptionSupported = (syncType, direction, capabilities) => {
    if (!capabilities || !capabilities[direction]) {
        return false;
    }

    const supportedTypes = capabilities[direction];
    
    // Map sync types to capability names
    const typeMapping = {
        'orders': ['orders', 'order', 'fulfillment', 'fulfillments'],
        'stock': ['stock', 'inventory', 'quantities'],
        'products': ['products', 'product', 'catalog', 'items'],
        'prices': ['prices', 'price', 'pricing']
    };

    const mappedTypes = typeMapping[syncType] || [syncType];
    
    return mappedTypes.some(type => 
        supportedTypes.some(supportedType => 
            supportedType.toLowerCase().includes(type.toLowerCase())
        )
    );
};

/**
 * Get sync option configuration with availability and descriptions
 * @param {Object} capabilities - Marketplace capabilities
 * @param {Function} t - Translation function
 * @returns {Array} - Array of sync option configurations
 */
export const getSyncOptionConfigurations = (capabilities, t) => {
    const syncOptions = [
        {
            key: 'syncOrders',
            type: 'orders',
            direction: 'receive',
            label: 'sync_orders',
            description: 'sync_orders_description',
            tooltip: 'sync_orders_tooltip',
            icon: '📦',
            newIcon:Inventory2OutlinedIcon,
            category: 'receive'
        },
        {
            key: 'syncStock',
            type: 'stock',
            direction: 'send',
            label: 'sync_stock',
            description: 'sync_stock_description',
            tooltip: 'sync_stock_tooltip',
            icon: '📋',
            newIcon:DescriptionOutlinedIcon,
            category: 'send'
        },
        // {
        //     key: 'syncProducts',
        //     type: 'products',
        //     direction: 'receive',
        //     label: 'sync_products',
        //     description: 'sync_products_description',
        //     tooltip: 'sync_products_tooltip',
        //     icon: '🏷️',
        //     category: 'receive'
        // },
        // {
        //     key: 'syncPrices',
        //     type: 'prices',
        //     direction: 'receive',
        //     label: 'sync_prices',
        //     description: 'sync_prices_description',
        //     tooltip: 'sync_prices_tooltip',
        //     icon: '💰',
        //     category: 'receive'
        // },
        {
            key: 'syncFulfillment',
            type: 'orders',
            direction: 'send',
            label: 'sync_fulfillment',
            description: 'sync_fulfillment_description',
            tooltip: 'sync_fulfillment_tooltip',
            icon: '🚚',
            newIcon:LocalShippingOutlinedIcon,
            category: 'send'
        }
    ];

    return syncOptions.map(option => ({
        ...option,
        available: isSyncOptionSupported(option.type, option.direction, capabilities),
        unavailableReason: !isSyncOptionSupported(option.type, option.direction, capabilities) 
            ? t('sync_option_not_supported_by_marketplace') 
            : null
    }));
};

/**
 * Process field configuration for marketplace
 * 
 * Maps API field format to component-expected format:
 * - API provides: fieldType, valueOptions.OauthUrl
 * - Component expects: type, oauth_url
 * 
 * @param {Object} marketplace - Marketplace connection definition
 * @returns {Array} - Processed field configurations
 */
export const processFieldConfigurations = (marketplace) => {
    if (!marketplace) return [];

    // Handle fields array (new format)
    if (marketplace.fields && Array.isArray(marketplace.fields)) {
        return marketplace.fields
            .filter(field => {
                // Filter out fields with no valid name/id
                const hasValidName = field && (field.name || field.id || field.key);
                if (!hasValidName) {
                    console.warn('Skipping field with no valid name/id:', field);
                    return false;
                }
                return true;
            })
            .map(field => {
                const validatedType = validateFieldType(field);
                const fieldName = field.name || field.id || field.key;
                
                // Ensure field name is a valid string
                if (typeof fieldName !== 'string' || fieldName.trim() === '') {
                    console.warn('Invalid field name detected:', fieldName, 'for field:', field);
                    return null;
                }
                
                return {
                    ...field,
                    name: fieldName,
                    id: fieldName,
                    required: field.required || false,
                    type: validatedType,
                    // Ensure OAuth URL is mapped correctly
                    oauth_url: field.valueOptions?.OauthUrl || field.oauth_url,
                    // Ensure dropdown options are mapped correctly  
                    valueOptions: field.valueOptions || field.options || [],
                    // Add default value handling
                    defaultValue: field.defaultValue || field.default_value || ''
                };
            })
            .filter(field => field !== null); // Remove null entries
    }

    // Handle required_fields array (legacy format)
    if (marketplace.required_fields && Array.isArray(marketplace.required_fields)) {
        return marketplace.required_fields
            .filter(fieldName => {
                // Filter out invalid field names
                const isValid = fieldName && typeof fieldName === 'string' && fieldName.trim() !== '';
                if (!isValid) {
                    console.warn('Skipping invalid required field name:', fieldName);
                    return false;
                }
                return true;
            })
            .map(fieldName => ({
                name: fieldName,
                id: fieldName,
                type: 'Text',
                required: true,
                label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                defaultValue: ''
            }));
    }

    return [];
};

/**
 * Validate sync configuration against marketplace capabilities
 * @param {Object} syncSettings - Current sync settings
 * @param {Object} capabilities - Marketplace capabilities
 * @param {Function} t - Translation function
 * @returns {Array} - Array of validation errors
 */
export const validateSyncConfiguration = (syncSettings, capabilities, t) => {
    const errors = [];
    const syncConfigs = getSyncOptionConfigurations(capabilities, t);

    syncConfigs.forEach(config => {
        const isEnabled = syncSettings[config.key] === 'true' || syncSettings[config.key] === true;
        
        if (isEnabled && !config.available) {
            errors.push({
                field: config.key,
                message: t('sync_option_not_available', { 
                    option: t(config.label),
                    reason: config.unavailableReason 
                })
            });
        }
    });

    return errors;
};

/**
 * Get default sync settings based on marketplace capabilities
 * @param {Object} capabilities - Marketplace capabilities
 * @returns {Object} - Default sync settings
 */
export const getDefaultSyncSettings = (capabilities) => {
    const syncConfigs = getSyncOptionConfigurations(capabilities, (key) => key);
    const defaults = {};

    syncConfigs.forEach(config => {
        // Enable supported options by default for common sync types
        if (config.available) {
            if (config.type === 'orders' || config.type === 'stock') {
                defaults[config.key] = 'true';
            } else {
                defaults[config.key] = 'false';
            }
        } else {
            defaults[config.key] = 'false';
        }
    });

    return defaults;
};

/**
 * Format marketplace name for display
 * @param {Object} marketplace - Marketplace connection definition
 * @returns {string} - Formatted marketplace name
 */
export const formatMarketplaceName = (marketplace) => {
    if (!marketplace) return '';
    
    return marketplace.display_name || 
           marketplace.name || 
           marketplace.title || 
           'Unknown Marketplace';
};

/**
 * Get marketplace description for display
 * @param {Object} marketplace - Marketplace connection definition
 * @returns {string} - Marketplace description
 */
export const getMarketplaceDescription = (marketplace) => {
    if (!marketplace) return '';
    
    return marketplace.description || 
           marketplace.summary || 
           marketplace.info || 
           '';
};

/**
 * Check if marketplace requires OAuth authentication
 * @param {Object} marketplace - Marketplace connection definition
 * @returns {boolean} - Whether OAuth is required
 */
export const requiresOAuth = (marketplace) => {
    if (!marketplace) return false;
    
    const fields = processFieldConfigurations(marketplace);
    return fields.some(field => field.type === 'OAuthButton');
};

/**
 * Get marketplace country/region information
 * @param {Object} marketplace - Marketplace connection definition
 * @returns {string} - Country/region information
 */
export const getMarketplaceRegion = (marketplace) => {
    if (!marketplace) return '';
    
    return marketplace.country || 
           marketplace.region || 
           marketplace.locale || 
           '';
};

/**
 * Create state update helper for sync settings
 * @param {Function} setState - State setter function
 * @param {Object} capabilities - Marketplace capabilities
 * @returns {Function} - State update helper
 */
export const createSyncStateUpdater = (setState, capabilities) => {
    return (key, value) => {
        setState(prevState => {
            const newState = { ...prevState, [key]: value };
            
            // Validate the new state against capabilities
            const errors = validateSyncConfiguration(newState, capabilities, (key) => key);
            
            // If there are errors, revert the change
            if (errors.length > 0) {
                console.warn('Sync configuration validation failed:', errors);
                return prevState;
            }
            
            return newState;
        });
    };
};